import { PropsWithChildren, ReactNode, useState } from 'react'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'

import { MeasurementType } from '../../_generated/api/tagged-hardware'
import DateDisplay from '../common/pure/DateDisplay'
import { Column, Row } from '../common/pure/FlexContainer'
import WaveGraph from '../graph/WaveGraph'
import { useUserContext } from '../contexts/UserContext'
import Measurement from '../../models/Measurement'
import Sensor from '../../models/Sensor'
import Device from '../../models/Device'
import { GenericStorage } from '../../tools/dom/storage'
import { toDecimalPlaces, degreeC, mm } from '../../tools/format'
import t, { _ta } from '../../tools/t'

const showReferenceStorage = new GenericStorage<boolean>('MEASUREMENT_DETAILS_SHOW_REFERENCE')

interface MeasurementDetailsDialogProps {
    measurement?: Measurement
    referenceMeasurement?: Measurement
    sensor?: Sensor
    device?: Device
    onExport?: () => void
    onHide: () => void
}
export default function MeasurementDetailsDialog({
    measurement,
    referenceMeasurement,
    sensor,
    device,
    onExport,
    onHide,
}: MeasurementDetailsDialogProps) {
    const { getUserNameById } = useUserContext()
    const [showReference, setShowReference] = useState(showReferenceStorage.read() ?? true)

    if (!measurement) return

    const reading = measurement.reading
    const settings = reading?.settings
    const calculation = measurement.normalized

    const isReference = measurement.type === MeasurementType.Reference
    const isElongation = measurement.type === MeasurementType.Elongation
    const reference = isReference ? measurement : referenceMeasurement
    const elongation = isElongation ? measurement : undefined

    const enableReferenceToggle =
        measurement.type === MeasurementType.Elongation && !!referenceMeasurement

    const onShowReferenceChange = (value: boolean) => {
        showReferenceStorage.store(value)
        setShowReference(value)
    }

    return (
        <Dialog
            visible={true}
            draggable={false}
            onHide={onHide}
            header={<MeasurementDetailsHeader />}
            footer={onExport ? <MeasurementDetailsFooter onExport={onExport} /> : undefined}
        >
            <Row className="measurement-details__container" gap={2}>
                <Column className="measurement-details__graph" alignment="center">
                    <WaveGraph
                        width={1050}
                        height={630}
                        strokeWidth={3}
                        showAxes={true}
                        primaryTrigger={
                            isReference || showReference ? reference?.trigger : undefined
                        }
                        primaryCoordinates={
                            isReference || showReference ? reference?.coordinates : undefined
                        }
                        secondaryCoordinates={elongation?.coordinates}
                        secondaryTrigger={elongation?.trigger}
                        animate={false}
                    />
                </Column>
                <Column className="measurement-details__data" gap={1.5}>
                    <MeasurementDetailsColumn title={t('common_measurement')}>
                        <MeasurementDetailRow
                            label={t('measurement_field_type')}
                            value={_ta(measurement, 'type')}
                        />

                        {enableReferenceToggle && (
                            <MeasurementDetailRow
                                label={t('measurement_field_toggle_reference')}
                                value={
                                    <Checkbox
                                        checked={showReference}
                                        onChange={(event) => onShowReferenceChange(!!event.checked)}
                                    />
                                }
                            />
                        )}

                        {calculation?.resultMm && (
                            <MeasurementDetailRow
                                label={t('measurement_field_result')}
                                value={mm(toDecimalPlaces(calculation.resultMm))}
                            />
                        )}

                        {measurement?.measuredAt && (
                            <MeasurementDetailRow
                                label={t('measurement_field_measured_at')}
                                value={
                                    <DateDisplay
                                        value={measurement.measuredAt}
                                        target={measurement.id}
                                    />
                                }
                            />
                        )}

                        {!measurement.isManual && reading?.temperatureC && (
                            <MeasurementDetailRow
                                label={t('measurement_field_temperature')}
                                value={degreeC(toDecimalPlaces(reading.temperatureC, 1))}
                            />
                        )}

                        {!measurement.isManual && reading?.quality && (
                            <MeasurementDetailRow
                                label={t('multimax_quality_score')}
                                value={
                                    !reading.quality.value || reading.quality.value === -1
                                        ? '—'
                                        : toDecimalPlaces(reading.quality.value, 2)
                                }
                            />
                        )}
                    </MeasurementDetailsColumn>

                    <MeasurementDetailsColumn title={t('common_settings')}>
                        {measurement.polarity && (
                            <MeasurementDetailRow
                                label={t('measurement_field_polarity')}
                                value={_ta(measurement, 'polarity')}
                            />
                        )}

                        {settings?.delayMm && (
                            <MeasurementDetailRow
                                label={t('measurement_field_delay')}
                                value={mm(settings.delayMm)}
                            />
                        )}

                        {settings?.gain && (
                            <MeasurementDetailRow
                                label={t('measurement_field_gain')}
                                value={settings.gain}
                            />
                        )}

                        {settings?.gateMm && (
                            <MeasurementDetailRow
                                label={t('measurement_field_gate')}
                                value={mm(settings.gateMm)}
                            />
                        )}

                        {settings?.threshold && (
                            <MeasurementDetailRow
                                label={t('measurement_field_threshold')}
                                value={settings.threshold}
                            />
                        )}

                        {settings?.rangeMm && (
                            <MeasurementDetailRow
                                label={t('measurement_field_range')}
                                value={mm(settings.rangeMm)}
                            />
                        )}
                    </MeasurementDetailsColumn>

                    <MeasurementDetailsColumn title={t('common_additional_info')}>
                        {measurement.measuredBy && (
                            <MeasurementDetailRow
                                label={t('common_user')}
                                value={getUserNameById(measurement.measuredBy)}
                            />
                        )}
                        {device && (
                            <>
                                <MeasurementDetailRow
                                    label={t('measurement_field_device_serial')}
                                    value={device.serialNo}
                                />
                                {/* NOTE: We no longer show the serial of the ultrasonic unit, but at some point we may need to display it again. */}
                                {/* <MeasurementDetailRow */}
                                {/*     label={t('measurement_field_multimax_serial')} */}
                                {/*     value={device.multiMaxSerialNo} */}
                                {/* /> */}
                                <MeasurementDetailRow
                                    label={t('measurement_field_device_software_version')}
                                    value={device.softwareVersion}
                                />
                            </>
                        )}
                        {sensor && (
                            <>
                                <MeasurementDetailRow
                                    label={t('measurement_field_sensor_serial')}
                                    value={sensor.serialNo}
                                />
                                <MeasurementDetailRow
                                    label={t('measurement_field_sensor_size')}
                                    value={_ta(sensor, 'size')}
                                />
                            </>
                        )}
                        {measurement.normalized.offsetMm && (
                            <MeasurementDetailRow
                                label={t('measurement_field_offset')}
                                value={mm(toDecimalPlaces(measurement.normalized.offsetMm, 3))}
                            />
                        )}
                    </MeasurementDetailsColumn>
                </Column>
            </Row>
        </Dialog>
    )
}

interface MeasurementDetailColumnProps extends PropsWithChildren, MeasurementDetailTitle {}
const MeasurementDetailsColumn = ({ title, children }: MeasurementDetailColumnProps) => (
    <Column gap={0.5}>
        <MeasurementDetailTitle title={title} />
        {children}
    </Column>
)

interface MeasurementDetailRow {
    label: string
    value: ReactNode
}
const MeasurementDetailRow = ({ label, value }: MeasurementDetailRow) => (
    <Row className="measurement-details-row" gap={1.5}>
        <div className="measurement-details-row__label">{label}</div>
        <div className="measurement-details-row__value">{value}</div>
    </Row>
)

interface MeasurementDetailTitle {
    title: string
}
const MeasurementDetailTitle = ({ title }: MeasurementDetailTitle) => (
    <Row className="measurement-details-row">
        <div className="measurement-details-row__title">{title}</div>
    </Row>
)

const MeasurementDetailsHeader = () => <>{t('measurement_details_header')}</>

interface MeasurementDetailsFooterProps {
    onExport: () => void
}
const MeasurementDetailsFooter = ({ onExport }: MeasurementDetailsFooterProps) => (
    <Row className="measurement-details__footer" arrangement="end">
        <Button onClick={onExport} label={t('common_export')} severity="secondary" text />
    </Row>
)
